import { type ComponentType, useEffect, useRef, useState } from 'react';

const RESEARCH_GRAPH_ROOT_MARGIN = '400px 0px';

export default function DeferredResearchGraph({
  featured = false,
}: {
  featured?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const importPromiseRef = useRef<Promise<{
    default: ComponentType<{ className?: string; featured?: boolean }>;
  }> | null>(null);
  const [ResearchGraph, setResearchGraph] = useState<ComponentType<{
    className?: string;
    featured?: boolean;
  }> | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadGraph = () => {
      importPromiseRef.current ??= import('./ResearchGraph');
      importPromiseRef.current
        .then((module) => {
          if (!cancelled) setResearchGraph(() => module.default);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    };

    if (importPromiseRef.current) {
      loadGraph();
      return () => {
        cancelled = true;
      };
    }

    const container = containerRef.current;
    if (!container || typeof IntersectionObserver === 'undefined') {
      loadGraph();
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        loadGraph();
      },
      { rootMargin: RESEARCH_GRAPH_ROOT_MARGIN },
    );
    observer.observe(container);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`bg-secondary relative min-h-[590px] min-w-0 rounded-2xl sm:min-h-[632px] ${featured ? 'xl:h-[clamp(546px,calc(61.666667vw-342px),842px)] xl:min-h-0' : ''}`}
    >
      {ResearchGraph ? (
        <ResearchGraph featured={featured} />
      ) : (
        <section
          className="absolute inset-0 rounded-2xl p-4 sm:p-6"
          aria-busy={!failed}
          aria-label="Research scaling graph"
        >
          <span className="sr-only">
            {failed
              ? 'Research scaling graph is unavailable.'
              : 'Loading research scaling graph…'}
          </span>
        </section>
      )}
    </div>
  );
}
